import { toAuthViewModel, toLoginRequest, toRegisterRequest } from '../../../core/auth/auth.core.js';
import { describeAuthError } from '../../../core/error.core.js';
import { NotificationType } from '../../../core/vocabulary.js';
import { t } from '../../../i18n/i18n.js';
import { errorText } from './error-text.js';

export class AuthEventAdapter {
  #authApiAdapter;
  #notificationComponent;
  #onAuthenticated;

  /**
   * @param {import('../../adapters/driven/auth.api-adapter.js').AuthApiAdapter} authApiAdapter
   * @param {HTMLElement} notificationComponent
   * @param {(auth: import('../../types/user.types.js').AuthViewModel) => void} onAuthenticated
   */
  constructor(authApiAdapter, notificationComponent, onAuthenticated) {
    this.#authApiAdapter = authApiAdapter;
    this.#notificationComponent = notificationComponent;
    this.#onAuthenticated = onAuthenticated;
  }

  connect() {
    document.addEventListener('auth-login', this.#onLogin);
    document.addEventListener('auth-register', this.#onRegister);
  }

  disconnect() {
    document.removeEventListener('auth-login', this.#onLogin);
    document.removeEventListener('auth-register', this.#onRegister);
  }

  #onLogin = async (event) => {
    const request = toLoginRequest(event.detail);
    try {
      const apiResponse = await this.#authApiAdapter.login(request);
      const auth = toAuthViewModel(apiResponse);
      // An error from an earlier failed attempt no longer applies once the user is in.
      this.#notificationComponent.dismissError();
      await this.#onAuthenticated(auth);
    } catch (error) {
      this.#notificationComponent.show(errorText(error, describeAuthError), NotificationType.ERROR);
    } finally {
      document.dispatchEvent(new CustomEvent('auth-request-complete'));
    }
  };

  #onRegister = async (event) => {
    const request = toRegisterRequest(event.detail);
    try {
      const apiResponse = await this.#authApiAdapter.register(request);
      const auth = toAuthViewModel(apiResponse);
      this.#notificationComponent.show(t('auth.notifyAccountCreated'), NotificationType.SUCCESS);
      await this.#onAuthenticated(auth);
    } catch (error) {
      this.#notificationComponent.show(errorText(error, describeAuthError), NotificationType.ERROR);
    } finally {
      document.dispatchEvent(new CustomEvent('auth-request-complete'));
    }
  };
}
